import React, { useState, useEffect } from "react";
import { format, parseISO, addYears, addDays, subDays } from "date-fns";
import {
  RefreshCw, Search, X, Loader, Plus, Eye, Briefcase, Phone,
  Clock, ChevronDown, MapPin, Building, Pencil, Trash2
} from "lucide-react";
import { GET, PUT, POST, DELETE } from "../services/api";
import { useMasterChapter, MasterChapterLockNote } from "../context/MasterChapter";

// --- Global Styles for Thin Scrollbar ---
const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    .animate-zoom-in { animation: zoom-in 0.3s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes zoom-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
  `}</style>
);

// Statuses come from the backend (utils/membership.util.js); "expiring" = ends within 30 days.
const STATUS = {
  active: { label: "Active", color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  expiring: { label: "Expiring", color: "bg-amber-50 text-amber-700 border-amber-100" },
  expired: { label: "Expired", color: "bg-red-50 text-red-700 border-red-100" },
  not_enrolled: { label: "Not enrolled", color: "bg-slate-100 text-slate-600 border-slate-200" },
};

const TABS = [
  { key: "due", label: "Renewals Due", match: (r) => r.status === "expiring" || r.status === "expired" },
  { key: "all", label: "All", match: () => true },
  { key: "active", label: "Active", match: (r) => r.status === "active" },
  { key: "expiring", label: "Expiring", match: (r) => r.status === "expiring" },
  { key: "expired", label: "Expired", match: (r) => r.status === "expired" },
  { key: "not_enrolled", label: "Not Enrolled", match: (r) => r.status === "not_enrolled" },
];

const PAYMENT_COLOR = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-100",
  pending: "bg-orange-50 text-orange-700 border-orange-100",
  unpaid: "bg-red-50 text-red-700 border-red-100",
};

const toInput = (d) => (d ? format(new Date(d), "yyyy-MM-dd") : "");
const showDate = (d) => (d ? format(new Date(d), "dd MMM yyyy") : "-");
const oneYearFrom = (start) => (start ? format(subDays(addYears(parseISO(start), 1), 1), "yyyy-MM-dd") : "");

const daysText = (r) => {
  if (r.status === "not_enrolled") return "";
  if (r.remainingDays < 0) return `${-r.remainingDays}d overdue`;
  if (r.remainingDays === 0) return "ends today";
  return `${r.remainingDays}d left`;
};

const inputCls = "w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800 disabled:opacity-50";
const labelCls = "text-xs font-bold text-slate-500 uppercase tracking-wide";

const Membership = () => {
  const master = useMasterChapter(); // super-admin master chapter filter
  const isSuperAdmin = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").role === "super-admin"; } catch { return false; }
  })();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("due");
  const [searchTerm, setSearchTerm] = useState("");

  // --- City / Chapter filter ---
  const [cities, setCities] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [cityId, setCityId] = useState(master.cityId || "");
  const [chapterId, setChapterId] = useState(master.chapterId || "");

  // --- Modals ---
  const [historyUserId, setHistoryUserId] = useState(null);
  const [periodForm, setPeriodForm] = useState(null); // { mode: 'create'|'renew'|'edit', row, yearId, ...fields }
  const [submitting, setSubmitting] = useState(false);
  const [planFee, setPlanFee] = useState(null); // single plan fee from Settings

  const historyRow = data.find((r) => r.userId === historyUserId);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cityId) params.append("cityId", cityId);
      if (chapterId) params.append("chapterId", chapterId);
      const response = await GET(`/membership?${params.toString()}`);
      if (response && response.success) setData(response.data || []);
    } catch (error) {
      console.error("Error fetching memberships:", error);
      alert(error.message || "Could not load memberships.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    GET("/membership/cities").then((r) => r?.success && setCities(r.data)).catch(() => {});
    GET("/membership/fee").then((r) => r?.success && setPlanFee(r.data.fee)).catch(() => {});
  }, []);

  // Master chapter locks the page filter; clearing it resets to "All"
  useEffect(() => {
    setCityId(master.cityId || "");
    setChapterId(master.chapterId || "");
  }, [master.chapterId, master.cityId]);

  useEffect(() => {
    setChapters([]);
    if (cityId) GET(`/membership/chapters/city/${cityId}`).then((r) => r?.success && setChapters(r.data)).catch(() => {});
  }, [cityId]);

  useEffect(() => {
    fetchData();
  }, [cityId, chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Period form (create / renew / edit) ---
  const openPeriodForm = (mode, row, year) => {
    let startDate = format(new Date(), "yyyy-MM-dd");
    if (mode === "renew" && row.current) startDate = format(addDays(new Date(row.current.endDate), 1), "yyyy-MM-dd");
    if (mode === "edit") startDate = toInput(year.startDate);

    setPeriodForm({
      mode,
      row,
      yearId: year?._id,
      startDate,
      endDate: mode === "edit" ? toInput(year.endDate) : oneYearFrom(startDate),
      price: mode === "edit" ? year.price : planFee ?? row.current?.price ?? "",
      paymentStatus: mode === "edit" ? year.paymentStatus : "paid",
      paidOn: mode === "edit" ? toInput(year.paidOn) : format(new Date(), "yyyy-MM-dd"),
      remark: mode === "edit" ? year.remark || "" : "",
    });
  };

  const setField = (field, value) =>
    setPeriodForm((f) => {
      const next = { ...f, [field]: value };
      // New periods default to one year; edited history keeps whatever end date was entered
      if (field === "startDate" && f.mode !== "edit") next.endDate = oneYearFrom(value);
      return next;
    });

  const handlePeriodSubmit = async (e) => {
    e.preventDefault();
    const { mode, row, yearId, startDate, endDate, price, paymentStatus, paidOn, remark } = periodForm;
    const body = { startDate, endDate, price: Number(price), paymentStatus, paidOn: paymentStatus === "paid" ? paidOn : "", remark };

    setSubmitting(true);
    try {
      const response =
        mode === "create" ? await POST("/membership", { userId: row.userId, ...body })
        : mode === "renew" ? await PUT(`/membership/renew/${row.membershipId}`, body)
        : await PUT(`/membership/${row.membershipId}/years/${yearId}`, body);
      alert(response?.message || "Saved.");
      setPeriodForm(null);
      fetchData();
    } catch (error) {
      alert(error.message || "Could not save membership.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePeriod = async (row, year) => {
    if (!window.confirm(`Delete the period ${showDate(year.startDate)} — ${showDate(year.endDate)}? This cannot be undone.`)) return;
    try {
      await DELETE(`/membership/${row.membershipId}/years/${year._id}`);
      fetchData();
    } catch (error) {
      alert(error.message || "Could not delete period.");
    }
  };

  // --- Filter Logic ---
  const search = searchTerm.toLowerCase();
  const searched = data.filter((r) =>
    [r.full_name, r.email, r.business_name, r.phone].some((v) => (v || "").toLowerCase().includes(search))
  );
  const tab = TABS.find((t) => t.key === activeTab);
  const filteredData = searched
    .filter(tab.match)
    .sort((a, b) => (a.remainingDays ?? Infinity) - (b.remainingDays ?? Infinity));

  return (
    <div>
      <CustomStyles />

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 animate-fade-in-up">

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-3 mb-4">
          <div className="relative w-full lg:w-56">
            <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
            <select
              className={`${inputCls} pl-9 appearance-none`}
              value={cityId}
              disabled={!!master.chapterId}
              onChange={(e) => { setCityId(e.target.value); setChapterId(""); }}
            >
              <option value="">All Cities</option>
              {cities.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
          </div>
          <div className="relative w-full lg:w-56">
            <Building className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
            <select
              className={`${inputCls} pl-9 appearance-none`}
              value={chapterId}
              disabled={!!master.chapterId || !cityId}
              onChange={(e) => setChapterId(e.target.value)}
            >
              <option value="">{cityId ? "All Chapters" : "Select city first"}</option>
              {chapters.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
          </div>
          <div className="relative w-full lg:flex-1">
            <input
              type="text"
              placeholder="Search name, phone, business..."
              className={`${inputCls} pl-10`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
          </div>
          <button onClick={fetchData} title="Refresh" className="p-3 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="mb-3"><MasterChapterLockNote /></div>

        {/* Tabs */}
        <div className="flex flex-wrap bg-slate-100 p-1 rounded-xl mb-4 w-full lg:w-fit gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === t.key ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              {t.label} <span className="ml-1 text-xs opacity-70">{searched.filter(t.match).length}</span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-slate-200 custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-200 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="p-4">Member</th>
                <th className="p-4">Location</th>
                <th className="p-4">Current Period</th>
                <th className="p-4">Last Renewed</th>
                <th className="p-4 text-center">Status</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr><td colSpan="6" className="p-12 text-center text-slate-400 font-medium">Loading membership data...</td></tr>
              ) : filteredData.length === 0 ? (
                <tr><td colSpan="6" className="p-12 text-center text-slate-400 font-medium">No members found.</td></tr>
              ) : (
                filteredData.map((r) => {
                  const st = STATUS[r.status];
                  return (
                    <tr key={r.userId} className="hover:bg-slate-50/80 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 shrink-0 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
                            {(r.full_name || "?").substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800 text-sm">{r.full_name}</p>
                            <div className="flex items-center gap-1 text-xs text-slate-500"><Briefcase className="w-3 h-3" />{r.business_name || "N/A"}</div>
                            <div className="flex items-center gap-1 text-xs text-slate-500"><Phone className="w-3 h-3" />{r.phone || "N/A"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-slate-600 font-medium">
                        {r.city_name || "-"}<div className="text-xs text-slate-400">{r.chapter_name || "-"}</div>
                      </td>
                      <td className="p-4 text-sm">
                        {r.current ? (
                          <>
                            <p className="font-bold text-slate-800">{showDate(r.current.startDate)} — {showDate(r.current.endDate)}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-500">₹{Number(r.current.price || 0).toLocaleString()}</span>
                              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${PAYMENT_COLOR[r.current.paymentStatus] || PAYMENT_COLOR.pending}`}>
                                {r.current.paymentStatus}
                              </span>
                            </div>
                          </>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {showDate(r.lastRenewedOn)}
                        {r.years.length > 0 && <div className="text-xs text-slate-400">{r.years.length} period{r.years.length > 1 ? "s" : ""}</div>}
                      </td>
                      <td className="p-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold border ${st.color}`}>{st.label}</span>
                        <div className="text-xs text-slate-400 mt-1">{daysText(r)}</div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {r.membershipId && (
                            <button onClick={() => setHistoryUserId(r.userId)} title="History" className="p-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100">
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {isSuperAdmin && (r.membershipId ? (
                            <button onClick={() => openPeriodForm("renew", r)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                              <RefreshCw className="w-3 h-3" /> Renew
                            </button>
                          ) : (
                            <button onClick={() => openPeriodForm("create", r)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                              <Plus className="w-3 h-3" /> Add
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- HISTORY MODAL --- */}
      {historyRow && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-zoom-in flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{historyRow.full_name}</h3>
                <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-slate-500 mt-1">
                  <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {historyRow.business_name || "No Business"}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {historyRow.city_name}, {historyRow.chapter_name}</span>
                  {historyRow.joining_date && <span>Joined {showDate(historyRow.joining_date)}</span>}
                  <span className={`px-2 py-0.5 rounded-full border font-bold ${STATUS[historyRow.status].color}`}>{STATUS[historyRow.status].label}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <button onClick={() => openPeriodForm("renew", historyRow)} className="flex items-center gap-1 px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                    <RefreshCw className="w-3 h-3" /> Renew
                  </button>
                )}
                <button onClick={() => setHistoryUserId(null)} className="p-2 rounded-full hover:bg-white text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" /> Renewal History
              </h4>
              {historyRow.years.length === 0 ? (
                <p className="text-center text-slate-400 py-10 font-medium bg-slate-50 rounded-xl border border-slate-100">No membership periods recorded.</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-100">
                      <tr>
                        <th className="p-4">Period</th>
                        <th className="p-4">Amount</th>
                        <th className="p-4">Payment</th>
                        <th className="p-4">Paid On</th>
                        <th className="p-4">Remark</th>
                        {isSuperAdmin && <th className="p-4 text-right">Action</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {historyRow.years.map((y, i) => (
                        <tr key={y._id || i} className="hover:bg-slate-50/50">
                          <td className="p-4 font-bold text-slate-700">{showDate(y.startDate)} — {showDate(y.endDate)}</td>
                          <td className="p-4 font-bold text-emerald-600">₹{Number(y.price || 0).toLocaleString()}</td>
                          <td className="p-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase border ${PAYMENT_COLOR[y.paymentStatus] || PAYMENT_COLOR.pending}`}>{y.paymentStatus}</span>
                          </td>
                          <td className="p-4 text-slate-600">{showDate(y.paidOn)}</td>
                          <td className="p-4 text-slate-500 max-w-[200px] truncate" title={y.remark}>{y.remark || "-"}</td>
                          {isSuperAdmin && (
                            <td className="p-4 text-right whitespace-nowrap">
                              <button onClick={() => openPeriodForm("edit", historyRow, y)} title="Edit" className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => handleDeletePeriod(historyRow, y)} title="Delete" className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- PERIOD MODAL (create / renew / edit) --- */}
      {periodForm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg animate-zoom-in flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">
                {{ create: "Add Membership", renew: "Renew Membership", edit: "Edit Membership Period" }[periodForm.mode]}
              </h3>
              <button onClick={() => setPeriodForm(null)} className="p-2 rounded-full hover:bg-slate-100 text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handlePeriodSubmit} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <p className="font-bold text-slate-800">{periodForm.row.full_name}</p>
                <p className="text-xs font-medium text-slate-500 mt-1">
                  {periodForm.row.business_name} · {periodForm.row.chapter_name}
                  {periodForm.mode === "renew" && periodForm.row.current && <> · current ends {showDate(periodForm.row.current.endDate)}</>}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className={labelCls}>Start Date</label>
                  <input type="date" required className={inputCls} value={periodForm.startDate} onChange={(e) => setField("startDate", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>End Date</label>
                  <input type="date" required min={periodForm.startDate} className={inputCls} value={periodForm.endDate} onChange={(e) => setField("endDate", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Amount (₹){periodForm.mode !== "edit" && planFee != null && <span className="normal-case font-medium text-slate-400"> · plan fee</span>}</label>
                  <input type="number" required min="0" className={inputCls} value={periodForm.price} onChange={(e) => setField("price", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className={labelCls}>Payment</label>
                  <div className="relative">
                    <select className={`${inputCls} appearance-none`} value={periodForm.paymentStatus} onChange={(e) => setField("paymentStatus", e.target.value)}>
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="unpaid">Unpaid</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
                  </div>
                </div>
                {periodForm.paymentStatus === "paid" && (
                  <div className="space-y-1.5 col-span-2">
                    <label className={labelCls}>Paid On</label>
                    <input type="date" className={inputCls} value={periodForm.paidOn} onChange={(e) => setField("paidOn", e.target.value)} />
                  </div>
                )}
                <div className="space-y-1.5 col-span-2">
                  <label className={labelCls}>Remark</label>
                  <input type="text" maxLength={200} placeholder="Receipt no., payment mode, notes..." className={inputCls} value={periodForm.remark} onChange={(e) => setField("remark", e.target.value)} />
                </div>
              </div>
              <button type="submit" disabled={submitting} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors flex justify-center items-center gap-2">
                {submitting ? <Loader className="animate-spin w-4 h-4" /> : "Save"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Membership;
